/**
 * Screens 4–6 — the goods DeCA create form, its blocked-generation state, and
 * the grouped variant.
 *
 * One form covers all three. The four `FormSectionCard`s mirror the PDF's own
 * sections 1–4; the "Agrupar envíos" toggle turns section 3 into a repeatable
 * list of consignments (BOE Sixth) while shipper, carrier and transport stay on
 * the document — which is what makes "same cargador and transportista for every
 * consignment" structurally impossible to violate rather than a rule someone has
 * to remember.
 *
 * **The server owns validation.** The rail on the right is a live hint computed
 * from what is on screen; the authoritative answer is the `422` with
 * `partyErrors` that `POST /decas` returns, and that is what populates the field
 * errors and the per-party summary. A checksum `WARNING` never blocks the
 * button.
 *
 * One note on the flow, because it is a deliberate reading of the contract:
 * `POST /decas/{id}/validate` needs a document id, so there is no way to
 * dry-run a form that has never been saved. Rather than invent an endpoint, the
 * primary action creates and generates in a single `POST /decas`
 * (`generate: true`) — which is also the path a TMS takes — and renders the
 * rejection. "Guardar borrador" is the same call with `generate: false`.
 *
 * This is the goods half of `DecaCreateScreen`, which is now a shell choosing
 * between this form and the passenger route sheet. The body below is the one
 * that shipped, moved across unchanged: a user who never touches the type
 * selector sees exactly the screen they saw before the selector existed.
 */

import { useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  errorsByField,
  unwrap,
  unwrapWithETag,
  ifMatch,
  type Deca,
  type DecaInput,
  type FieldError as ApiFieldError,
  type PartyErrors,
  type UnidadMedida,
  UNIDADES_MEDIDA,
} from '@deca/api'

import { api } from '../app/api.ts'
import { isConflict, isExternalRefTaken, apiErrorMessage, validationProblemOf } from '../app/errors.ts'
import { useDeadAddresses, useInvalidateDeca } from '../app/queries.ts'
import { isEmailAddress } from '../deca/emailAddress.ts'
import { MAX_DECA_RECIPIENTS } from '../deca/emailRecipients.ts'
import { EmailTagsInput } from '../ui/EmailTagsInput.tsx'
import { useAuth } from '../app/auth.tsx'
import { downloadWindowDays, isGenerated } from '../deca/estado.ts'
import {
  FIELDS,
  byFormPosition,
  fieldDef,
  fieldsForSection,
  qualifiedFieldLabel,
} from '../deca/fields.ts'
import { joinServiceDate, splitServiceDate, localInputToRfc3339 } from '../deca/format.ts'
import { Button, Card, Datum, IconButton, Notice } from '../ui/primitives.tsx'
import { SoyYoAction, type PartyColumns } from '../ui/SoyYoAction.tsx'
import { partyField } from '../ui/partyFill.tsx'
import { SaveToBookAction } from '../ui/SaveToBookAction.tsx'
import { SettingBox } from '../ui/SettingBox.tsx'
import { EstadoChip } from '../ui/EstadoChip.tsx'
import {
  DecaFormProvider,
  FormField,
  FormSectionCard,
  MissingFieldsButton,
  TextInput,
} from '../ui/form.tsx'
import { ValidationSummary } from '../ui/ValidationSummary.tsx'
import { Icon } from '../ui/Icon.tsx'
import { useToast } from '../ui/Toast.tsx'
import type { BookField } from '../ui/bookField.ts'

interface ConsignmentValues {
  origen_domicilio: string
  destino_domicilio: string
  naturaleza_mercancia: string
  peso_magnitud: number | string
  unidad_medida: UnidadMedida | ''
}

/** Exported alongside `toInput`, which cannot be called from a test without it. */
export interface CreateFormValues {
  externalRef: string
  cargador_razon_social: string
  cargador_nif: string
  cargador_domicilio: string
  transportista_razon_social: string
  transportista_nif: string
  transportista_domicilio: string
  fecha_realizacion: string
  /**
   * `HH:MM`, or empty. Its own value rather than half of a `datetime-local` string, because HTML
   * has no control for "a date and possibly a time" and the emptiness is the whole point: Art. 6(f)
   * of the consolidated Order requires the *fecha*, and the Client withdrew the hour on 2026-09-18.
   */
  fecha_realizacion_hora: string
  matricula_tractor: string
  matricula_remolque: string
  /** «Rígido» (2026-10-01): no trailer, so `matricula_remolque` is neither asked for nor sent. */
  rigido: boolean
  autorizacion_especial_circulacion: string
  observaciones: string
  nif_conductor: string
  /**
   * «Enviar DeCA a» and «Enviar automáticamente al generar el DeCA» (#219): filled from the
   * shipper's book entry when it is chosen, changed here for this DeCA only.
   */
  emailRecipients: string[]
  emailAutoSend: boolean
  consignments: ConsignmentValues[]
}

/**
 * The two parties' columns, as «Soy yo» fills them: from a section card's header when there is one
 * consignment, and from its cell in «Datos comunes» when grouping (#137). The address book reads and
 * writes the same three (#159): the type-ahead on each name and NIF, and «Guardar en la libreta»
 * beside «Soy yo». One definition for every one of them, spelled out in full rather than built from
 * the section name: see `PartyColumns`.
 */
const PARTY_COLUMNS = {
  cargador: {
    name: 'cargador_razon_social',
    nif: 'cargador_nif',
    address: 'cargador_domicilio',
    // #219: the shipper's book entry carries who its DeCAs are emailed to.
    recipients: { list: 'emailRecipients', autoSend: 'emailAutoSend' },
  },
  transportista: {
    name: 'transportista_razon_social',
    nif: 'transportista_nif',
    address: 'transportista_domicilio',
  },
} as const satisfies Record<'cargador' | 'transportista', PartyColumns>

const EMPTY_CONSIGNMENT: ConsignmentValues = {
  origen_domicilio: '',
  destino_domicilio: '',
  naturaleza_mercancia: '',
  peso_magnitud: '',
  unidad_medida: '',
}

const DEFAULTS: CreateFormValues = {
  externalRef: '',
  cargador_razon_social: '',
  cargador_nif: '',
  cargador_domicilio: '',
  transportista_razon_social: '',
  transportista_nif: '',
  transportista_domicilio: '',
  fecha_realizacion: '',
  fecha_realizacion_hora: '',
  matricula_tractor: '',
  matricula_remolque: '',
  rigido: false,
  autorizacion_especial_circulacion: '',
  observaciones: '',
  nif_conductor: '',
  emailRecipients: [],
  emailAutoSend: false,
  consignments: [{ ...EMPTY_CONSIGNMENT }],
}

/**
 * The inverse of `toInput` — #90, so a draft can reopen in the form that made it.
 *
 * Four of these are conversions rather than copies, and each undoes a line below: the trailer
 * plate comes back from `null` as `''` (React logs a controlled-input warning on `null` and the
 * box will not type), the weight comes back from a number as the string the control carries, and
 * the one stored instant plus `fechaRealizacionHasTime` becomes a date box and an hour box that
 * may be empty. The flag is read, never inferred: a date-only document is stored as that day's
 * 00:00 in Madrid and 00:00 is also a real departure time.
 *
 * Every field takes `?? ''` because a draft is incomplete by definition — which is the whole
 * point of the issue, and is new since the draft button stopped requiring a complete form.
 */
/**
 * A read carries the unit as text (the contract, 2026-10-27), since the units a write accepts may
 * grow. The portal ships with the server, so a stored unit is one the select offers; one that is not
 * opens empty, as a draft with no unit chosen, rather than as a value the select has no option for.
 */
function isUnidadMedida(unit: string | null | undefined): unit is UnidadMedida {
  return (UNIDADES_MEDIDA as readonly string[]).includes(unit ?? '')
}

export function fromDeca(deca: Deca): CreateFormValues {
  const { date, time } = splitServiceDate(deca.fecha_realizacion, deca.fechaRealizacionHasTime)
  const rows: ConsignmentValues[] = (deca.consignments ?? []).map((row) => ({
    origen_domicilio: row.origen_domicilio ?? '',
    destino_domicilio: row.destino_domicilio ?? '',
    naturaleza_mercancia: row.naturaleza_mercancia ?? '',
    peso_magnitud: row.peso_magnitud == null ? '' : String(row.peso_magnitud),
    unidad_medida: isUnidadMedida(row.unidad_medida) ? row.unidad_medida : '',
  }))
  return {
    externalRef: deca.externalRef ?? '',
    cargador_razon_social: deca.cargador_razon_social ?? '',
    cargador_nif: deca.cargador_nif ?? '',
    cargador_domicilio: deca.cargador_domicilio ?? '',
    transportista_razon_social: deca.transportista_razon_social ?? '',
    transportista_nif: deca.transportista_nif ?? '',
    transportista_domicilio: deca.transportista_domicilio ?? '',
    fecha_realizacion: date,
    fecha_realizacion_hora: time,
    matricula_tractor: deca.matricula_tractor ?? '',
    matricula_remolque: deca.matricula_remolque ?? '',
    rigido: deca.rigido ?? false,
    autorizacion_especial_circulacion: deca.autorizacion_especial_circulacion ?? '',
    observaciones: deca.observaciones ?? '',
    nif_conductor: deca.nif_conductor ?? '',
    emailRecipients: [...(deca.emailRecipients ?? [])],
    emailAutoSend: deca.emailAutoSend ?? false,
    // One empty row rather than none: `ConsignmentBlock` renders index 0 unconditionally, so a
    // draft saved with no consignments would leave the whole of section 3 unrendered.
    consignments: rows.length ? rows : [{ ...EMPTY_CONSIGNMENT }],
  }
}

/**
 * A copy of an existing document, for «Duplicar» — #178. `fromDeca`'s values with the two that
 * cannot travel cleared: the transport date, which the user must choose again (the hour is kept,
 * Aleksey 2026-10-06), and `externalRef`, which names one transport in the company's own systems
 * (#92: one supersede chain). A copy is a new transport, so it starts without one, whatever the
 * source's status: while the source's chain stands, keeping it would be refused
 * `EXTERNAL_REF_TAKEN`, and once that chain is cancelled the reference is the TMS's to issue that
 * transport again. Everything else is the source's current state, as `GET` returns it after
 * amendments: what a copy must not carry (status, number, QR, CSV, versions, delivery
 * confirmations) is not form data, so it never reaches the form.
 */
export function copyOfDeca(source: Deca): CreateFormValues {
  return { ...fromDeca(source), externalRef: '', fecha_realizacion: '' }
}

/**
 * The form's values as the contract wants them.
 *
 * `grouped` is a parameter rather than a closed-over binding, because the only
 * thing standing between a consignment the user toggled away and a generated
 * PDF is the `slice(0, 1)` on the next line. Grouping appends a consignment and
 * leaving grouping does not remove one: the row vanishes from the screen —
 * `ConsignmentBlock` renders index 0 only — and stays in react-hook-form's
 * state, where `shouldUnregister` defaults to false. A payload carrying it does
 * not look broken from the server's side; it is a complete consignment with its
 * own origin, destination and goods, and it would be printed onto a document a
 * driver hands to an inspector, describing a shipment that is not on the lorry.
 *
 * Exported so its per-field rules can be tested directly: an empty trailer plate
 * is sent as `null`, and an unchosen unit or an untyped weight is not sent at
 * all. None of them is merely defensive. They were written when every submit had
 * already passed the mandatory rules; since #90 *Guardar borrador* sends whatever
 * has been filled in, so each one decides what a half-finished draft stores. The
 * unit's used to be `|| 'kg'`, and it stored a unit nobody had chosen.
 */
export function toInput(raw: CreateFormValues, grouped: boolean): DecaInput {
  const rows = grouped ? raw.consignments : raw.consignments.slice(0, 1)
  return {
    ...(raw.externalRef ? { externalRef: raw.externalRef } : {}),
    cargador_razon_social: raw.cargador_razon_social,
    cargador_nif: raw.cargador_nif,
    cargador_domicilio: raw.cargador_domicilio,
    transportista_razon_social: raw.transportista_razon_social,
    transportista_nif: raw.transportista_nif,
    transportista_domicilio: raw.transportista_domicilio,
    // The controls have no offset and the contract rejects a value without one; the wall-clock
    // time is Spanish, so that is the zone applied. An empty time box means a calendar day, sent
    // as that day's midnight in Madrid — the flag beside it is what says so, because midnight is
    // also a real departure time.
    fecha_realizacion: localInputToRfc3339(
      joinServiceDate(raw.fecha_realizacion, raw.fecha_realizacion_hora),
    ),
    fechaRealizacionHasTime: raw.fecha_realizacion_hora !== '',
    matricula_tractor: raw.matricula_tractor,
    // A rigid truck sends no plate, whatever the box last held, and says `rigido` out loud either
    // way: omitted, a draft PATCH would leave an unticked draft rigid (spec D11).
    matricula_remolque: raw.rigido ? null : raw.matricula_remolque || null,
    rigido: raw.rigido,
    autorizacion_especial_circulacion: raw.autorizacion_especial_circulacion || null,
    observaciones: raw.observaciones || null,
    nif_conductor: raw.nif_conductor,
    // Both always: a draft's PATCH with the list emptied clears what an earlier save stored (#219).
    emailRecipients: raw.emailRecipients,
    emailAutoSend: raw.emailAutoSend,
    consignments: rows.map((row) => {
      const peso = Number(row.peso_magnitud)
      return {
        origen_domicilio: row.origen_domicilio,
        destino_domicilio: row.destino_domicilio,
        naturaleza_mercancia: row.naturaleza_mercancia,
        // 🚨 Omitted, never sent, when no weight has been typed — #91. The control registers with
        // `valueAsNumber`, so an empty box is `NaN` here whatever `ConsignmentValues` declares, and
        // `JSON.stringify` writes `NaN` as **`null`**. That `null` reached a `numeric` column as
        // the string `'null'` and answered the user *"Error interno"* on the most ordinary action
        // this form offers: saving a draft they have barely started.
        //
        // The cast is the contract's, not ours: `ConsignmentInput` lists `peso_magnitud` in
        // `required` and types it `number`, so a draft with no weight yet has no legal shape to
        // send. The list binds at generation — the schema now says so — and this is a caller
        // sending the subset it has. Omission is also what *clears* it on a `PATCH`, since
        // `resolveConsignment` carries forward only the date and the remarks.
        ...(Number.isFinite(peso) ? { peso_magnitud: peso } : {}),
        // Sent only when chosen — #90. Every consignment starts with no unit and both generation
        // gates require one, so an empty unit here is a draft saved before it was picked. This
        // line used to answer that with `kg`, which the reopened draft then showed as a choice
        // and the next *Generar* passed through both gates. Omitted, the column stays `NULL` on
        // create and is cleared on a `PATCH`, exactly like the weight above.
        ...(row.unidad_medida ? { unidad_medida: row.unidad_medida } : {}),
      } as DecaInput['consignments'][number]
    }),
  }
}

/**
 * The form's three modes: creating, editing a draft (#90) and copying a document (#178). A union, so
 * no caller can pass `draft` and `source` together: the values and the submit would follow the
 * draft, a `PATCH`, while the banner named a copy.
 */
type GoodsCreateFormProps =
  | { draft: Deca; etag?: string; source?: never }
  | { source: Deca; draft?: never; etag?: never }
  | { draft?: undefined; etag?: undefined; source?: undefined }

/**
 * `draft` puts the form in editing mode — #90. `source` puts it in copying mode — #178: filled from
 * an existing document by `copyOfDeca`, it submits as an ordinary create, so the copy gets its own
 * number, QR and CSV and the source is never written. With neither, it is the create form it has
 * always been. The document's family is fixed by the route that mounts this (`/decas/$decaId/editar`
 * and `/decas/$decaId/duplicar` are goods URLs), so there is no type selector here and nothing to
 * discriminate at runtime.
 */
export function GoodsCreateForm({
  draft,
  etag,
  source,
}: GoodsCreateFormProps = {}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const invalidate = useInvalidateDeca()
  const { principal } = useAuth()

  const form = useForm<CreateFormValues>({
    defaultValues: draft ? fromDeca(draft) : source ? copyOfDeca(source) : DEFAULTS,
    mode: 'onBlur',
  })
  const consignments = useFieldArray({ control: form.control, name: 'consignments' })

  // 🚨 Seeded from the draft, not `false` — #90. `toInput` slices to the first consignment when
  // this is off and `DecaPatch.consignments` REPLACES the whole set, so a grouped draft
  // reopening ungrouped would delete its other legs on the first save. Derived from the array
  // that would be lost rather than from the server's `isGrouped`, because the two cannot then
  // disagree about the very data this protects.
  // A copy is seeded the same way (#178): a grouped source opens grouped, with every consignment.
  const [grouped, setGrouped] = useState(((draft ?? source)?.consignments?.length ?? 0) > 1)
  const [serverErrors, setServerErrors] = useState<Record<string, ApiFieldError>>({})
  const [partyErrors, setPartyErrors] = useState<PartyErrors[] | undefined>()
  const [penaltyNotice, setPenaltyNotice] = useState<{ amountEur?: number; perParty?: boolean }>()
  const [submitError, setSubmitError] = useState<string | null>(null)
  /**
   * The reference the server refused as another document's — #92. The field's mark is drawn from
   * this value, not held as react-hook-form state. A `setError` on a field with no rules is taken
   * away by the next blur while the refused value is still in the box; a `validate` rule keyed to
   * the value (#92's review) kept the mark but stopped *Generar* in the browser, so the same
   * reference never reached the server again — though deleting a draft gives its reference back.
   * Drawn from state it is only a mark: it lasts while the refused value is in the box, returns if
   * the user goes back to it, and every submit still asks the server, which owns the answer.
   */
  const [refusedRef, setRefusedRef] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /**
   * The draft's version as this form last wrote it: the `ETag` it was mounted with, then the one
   * each `PATCH` returns — #132. A refused *Generar* keeps the form open after its `PATCH` has moved
   * the version, so the next submit has to carry that `PATCH`'s `ETag`, or it is a 412 over a draft
   * nobody else touched. This is not the silent re-read the conflict branch below refuses: the
   * values on screen are exactly the ones that `PATCH` stored, and a real conflict still answers 412.
   */
  const [draftEtag, setDraftEtag] = useState<string | null | undefined>(etag)

  const values = form.watch()
  const externalRefMark =
    refusedRef !== null && values.externalRef === refusedRef ? t('errors.code.EXTERNAL_REF_TAKEN') : undefined

  /** Local, optimistic per-party progress — a hint, never the gate. */
  const progress = useMemo(() => mandatoryProgress(values, grouped), [values, grouped])
  const complete = progress.missing.length === 0
  /** What the rail is listing, for the phone's count button: the server's list once it has answered. */
  const missingCount = partyErrors
    ? partyErrors.flatMap((party) => party.fields).filter((field) => field.severity !== 'WARNING').length
    : progress.missing.length
  /** The rail's validation card, where that button takes the user — see `MissingFieldsButton`. */
  const validationCard = useRef<HTMLDivElement>(null)

  async function submit(raw: CreateFormValues, generate: boolean) {
    // The tags mark an address the server would not take; it would refuse the document with it (#219).
    if (raw.emailRecipients.some((address) => !isEmailAddress(address))) {
      setSubmitError(t('create.email.invalid'))
      return
    }
    setBusy(true)
    setSubmitError(null)
    setServerErrors({})
    setPartyErrors(undefined)
    setRefusedRef(null)
    try {
      // #90 — an existing draft is AMENDED, never re-created. A second `POST /decas` would
      // burn another number from the service-wide counter and leave the first draft behind,
      // and on the generate path it would issue a whole second document with its own URL and
      // its own QR. The contract calls `updateDecaDraft` a genuine partial update; `If-Match`
      // is required on it, and `GET /decas/{id}` is the only place an `ETag` is handed out, so
      // the read has to carry its version into this write.
      let created: Deca
      if (draft) {
        const amended = await unwrapWithETag(
          api.PATCH('/api/v1/decas/{decaId}', {
            params: { path: { decaId: draft.id }, header: ifMatch(draftEtag) },
            // 🚨 `externalRef` is sent explicitly here, and only here. `toInput` omits an empty
            // one, which is right on a create — the alternative is storing an explicit null
            // where the caller said nothing — and wrong on a PATCH, where an omitted key means
            // "unchanged". Without this line a user can set an external reference on a draft but
            // never clear it, and the screen would tell them the draft was saved.
            body: { ...toInput(raw, grouped), externalRef: raw.externalRef || null },
          }),
        )
        // Kept before generating, so a refused *Generar* leaves the form on the version it wrote.
        setDraftEtag(amended.etag)
        // 🚨 The amendment moved the version, so generation must carry the `ETag` the PATCH
        // *returned*. Re-using the one this form was mounted with is a 412 every time.
        created = generate
          ? await unwrap(
              api.POST('/api/v1/decas/{decaId}/generate', {
                params: { path: { decaId: draft.id }, header: ifMatch(amended.etag) },
              }),
            )
          : amended.data
      } else {
        created = await unwrap(
          api.POST('/api/v1/decas', { body: { ...toInput(raw, grouped), generate } }),
        )
      }
      invalidate(created.id)

      // Report what came back, not what was asked for. A document that is still
      // `BORRADOR` has no PDF, no QR and no direct-download URL, so telling the user it
      // was generated sends them to hand a driver a document that does not exist. The
      // request is not evidence of the outcome; the response is.
      if (generate && !isGenerated(created)) {
        toast.error(t('create.notGenerated'))
      } else {
        toast.success(
          generate
            ? t('create.generated', { documentNumber: created.documentNumber ?? '' })
            : t('create.savedDraft'),
        )
      }
      void navigate({ to: '/decas/$decaId', params: { decaId: created.id } })
    } catch (error) {
      const problem = validationProblemOf(error)
      if (problem) {
        setServerErrors(errorsByField(problem))
        setPartyErrors(problem.partyErrors)
        setPenaltyNotice(problem.penaltyNotice)
        setSubmitError(null)
      } else if (isConflict(error)) {
        // 🚨 #90 — the draft moved under this form. Say so in the words for it: a user who
        // reads a generic "no se ha podido guardar" has no reason to reload, and will retype
        // over the newer version.
        //
        // ⚠️ And DELIBERATELY not a silent re-read, though the issue's wording invites one.
        // `useForm` applies `defaultValues` on mount only, so refreshing the draft behind this
        // form would swap in a newer ETag while leaving the user's stale values on screen —
        // turning "refuses to overwrite" into "overwrites on the second click". Reloading has
        // to be the user's own act, which is exactly what the message asks for.
        setSubmitError(t('modify.versionConflict'))
      } else {
        // #92 — refused for its reference. On the field as well as in the notice, because the
        // field sits near the foot of the form and the notice at its head.
        if (isExternalRefTaken(error)) setRefusedRef(raw.externalRef)
        setSubmitError(apiErrorMessage(error, t))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <FormProvider {...form}>
      <DecaFormProvider value={{ serverErrors }}>
        <form className="stack create-form" onSubmit={(event) => event.preventDefault()}>
          {/* #178 — what this form is a copy of, and the one thing the copy still needs. A draft is
              shown as «Borrador de DeCA» until it is generated, never by its number (the detail
              screen's rule), so a copy of one is named as a draft. */}
          {source ? (
            <Notice tone="info">
              {`${
                isGenerated(source)
                  ? t('duplicate.copyOf', { documentNumber: source.documentNumber ?? '' })
                  : t('duplicate.copyOfDraft')
              } ${t('duplicate.chooseDate')}`}
            </Notice>
          ) : null}

          {/* The mode is a segmented control, not a checkbox buried in a
              section: grouping changes what the whole screen is for. */}
          <div className="segmented" role="group" aria-label={t('create.modeLabel')}>
            <button
              type="button"
              className="segmented__option"
              aria-pressed={!grouped}
              onClick={() => {
                setGrouped(false)
                // The click that switched the mode has just blurred the field the form opened on,
                // and `mode: 'onBlur'` marked it «Obligatorio» before anyone typed in it. The switch
                // is not the user leaving that field; the marks return on the next blur or on
                // Generar, and the rail still counts what is missing.
                form.clearErrors()
              }}
            >
              {t('create.modeSingle')}
            </button>
            <button
              type="button"
              className="segmented__option"
              aria-pressed={grouped}
              onClick={() => {
                setGrouped(true)
                // Not focused: `append` puts the cursor in the row it adds, and this one is added
                // on the way in, so the page scrolled past «Datos comunes» to the second «Lugar de
                // origen» (Paco, 2026-09-30). Grouping starts on the Cargador contractual instead,
                // by the `autoFocus` in «Datos comunes».
                if (consignments.fields.length === 1)
                  consignments.append({ ...EMPTY_CONSIGNMENT }, { shouldFocus: false })
                // Not marked either: see «Envío único» above.
                form.clearErrors()
              }}
            >
              {t('create.modeGrouped')}
            </button>
          </div>

          <Notice tone="info">
            {grouped ? t('create.groupBanner') : t('create.penaltyBanner', { amount: 401 })}
          </Notice>

          <div className="form-layout">
            <div className="stack">
              {submitError ? <Notice tone="danger">{submitError}</Notice> : null}

              {grouped ? (
                <>
                  <Card
                    title={t('create.commonData')}
                    subtitle={t('create.commonDataHelp', { count: consignments.fields.length })}
                  >
                    <div className="form-grid form-grid--full">
                      {/* Both parties share this card, so their labels carry
                          whose they are — there is no numbered section here
                          to say it for them. Each party's «Soy yo» follows its
                          NIF (#137): the empty third column on a desktop, and
                          NIF → Soy yo → Domicilio in the one column of a phone.
                          «Guardar en la libreta» shares that cell (#159). */}
                      {(['cargador', 'transportista'] as const).flatMap((party) =>
                        fieldsForSection(party).flatMap((field) => [
                          <FormField
                            key={field.column}
                            column={field.column}
                            full={field.control === 'textarea'}
                            qualified
                            autoFocus={field.no === 1}
                            addressBook={partyField(PARTY_COLUMNS[party], field.column)}
                          />,
                          ...(field.column === PARTY_COLUMNS[party].nif
                            ? [<SoyYoCell key={`${party}-soy-yo`} party={party} />]
                            : []),
                        ]),
                      )}
                      <ServiceDateFields />
                      <TransportFields />
                      <TextInput
                        label={t('fields.externalRef.label')}
                        optional
                        help={t('fields.externalRef.help')}
                        error={externalRefMark}
                        {...form.register('externalRef')}
                      />
                    </div>
                  </Card>

                  <Card
                    title={t('create.groupedConsignments')}
                    subtitle={t('group.orderMeaning')}
                    actions={
                      <span className="chip chip--info">{consignments.fields.length}</span>
                    }
                    flush
                  >
                    <ConsignmentTable
                      rows={consignments.fields}
                      onRemove={(index) => consignments.remove(index)}
                      canRemove={consignments.fields.length > 1}
                    />
                    <div className="card__body">
                      <Button
                        icon="plus"
                        onClick={() => consignments.append({ ...EMPTY_CONSIGNMENT })}
                        disabled={consignments.fields.length >= 50}
                      >
                        {t('create.addConsignment')}
                      </Button>
                    </div>
                  </Card>
                </>
              ) : (
                <>
                  <FormSectionCard
                    section="cargador"
                    actions={
                      <div className="party-actions">
                        <SoyYoAction columns={PARTY_COLUMNS.cargador} />
                        <SaveToBookAction columns={PARTY_COLUMNS.cargador} />
                      </div>
                    }
                  >
                    <div className="form-grid form-grid--full">
                      {fieldsForSection('cargador').map((field) => (
                        <FormField
                          key={field.column}
                          column={field.column}
                          full={field.control === 'textarea'}
                          autoFocus={field.no === 1}
                          addressBook={partyField(PARTY_COLUMNS.cargador, field.column)}
                        />
                      ))}
                    </div>
                  </FormSectionCard>

                  <FormSectionCard
                    section="transportista"
                    actions={
                      <div className="party-actions">
                        <SoyYoAction columns={PARTY_COLUMNS.transportista} />
                        <SaveToBookAction columns={PARTY_COLUMNS.transportista} />
                      </div>
                    }
                  >
                    <div className="form-grid form-grid--full">
                      {fieldsForSection('transportista').map((field) => (
                        <FormField
                          key={field.column}
                          column={field.column}
                          full={field.control === 'textarea'}
                          addressBook={partyField(PARTY_COLUMNS.transportista, field.column)}
                        />
                      ))}
                    </div>
                  </FormSectionCard>

                  <FormSectionCard section="envio">
                    <div className="stack">
                      <div className="form-grid form-grid--full">
                        <ServiceDateFields />
                      </div>
                      <ConsignmentBlock index={0} />
                    </div>
                  </FormSectionCard>

                  <FormSectionCard section="transporte">
                    <div className="form-grid form-grid--full">
                      <TransportFields />
                      <TextInput
                        label={t('fields.externalRef.label')}
                        optional
                        help={t('fields.externalRef.help')}
                        error={externalRefMark}
                        {...form.register('externalRef')}
                      />
                    </div>
                  </FormSectionCard>
                </>
              )}

              <EmailCard />
            </div>

            <aside className="form-layout__rail" aria-label={t('create.railTitle')}>
              {grouped ? (
                <Card title={t('create.groupValidTitle')}>
                  <ul className="check-list">
                    <li>
                      <Icon name="checkCircle" size={18} />
                      {t('create.groupCheckCargador')}
                    </li>
                    <li>
                      <Icon name="checkCircle" size={18} />
                      {t('create.groupCheckTransportista')}
                    </li>
                    <li>
                      <Icon name="checkCircle" size={18} />
                      {t('create.groupCheckSeparate', { count: consignments.fields.length })}
                    </li>
                  </ul>
                </Card>
              ) : null}

              <div ref={validationCard} tabIndex={-1} className="form-layout__anchor">
                <Card title={t('create.railTitle')}>
                  {partyErrors ? (
                    <ValidationSummary
                      partyErrors={partyErrors}
                      penaltyNotice={penaltyNotice}
                      okMessage={t('create.railReady')}
                    />
                  ) : (
                    <div className="stack-md">
                      <ProgressRow entry={progress} />
                      {complete ? null : (
                        <Notice tone="warning">
                          {t('create.railPending', { count: progress.missing.length })}{' '}
                          {t('party.penaltyExplain', { amount: 401 })}
                        </Notice>
                      )}
                    </div>
                  )}
                </Card>
              </div>

              {/* The primary action lives here and stays disabled until the
                  form is complete, as the frame has it — the TopBar's copy
                  was enabled and relied on the submit to refuse. */}
              <Card title={t('create.documentTitle')}>
                <div className="stack-sm small">
                  <div className="row-between">
                    <span className="muted">{t('list.columns.estado')}</span>
                    <EstadoChip estado="BORRADOR" />
                  </div>
                  <Datum
                    label={t('detail.type')}
                    value={
                      grouped
                        ? t('detail.typeGrouped', { count: consignments.fields.length })
                        : t('detail.typeSingle')
                    }
                  />
                  <Datum label={t('create.format')} value={t('create.formatValue')} />
                  <Datum
                    label={t('common.download')}
                    value={t('create.downloadValue', { count: downloadWindowDays(principal) })}
                  />

                  {/* #111 — one group, so a phone can pin it to the foot of the screen
                      (responsive/forms.css). On a desktop it is the same column of buttons. */}
                  <div className="stack-sm create-actions">
                    <MissingFieldsButton
                      count={missingCount}
                      refused={partyErrors !== undefined}
                      target={validationCard}
                    />
                    <Button
                      variant="primary"
                      icon="check"
                      block
                      loading={busy}
                      disabled={busy || !complete}
                      onClick={() => void form.handleSubmit((raw) => submit(raw, true))()}
                    >
                      {busy
                        ? t('create.generating')
                        : grouped
                          ? t('create.generateGrouped')
                          : t('create.generate')}
                    </Button>
                    {complete ? null : (
                      <p className="small muted">{t('create.generateEnabledWhen')}</p>
                    )}
                    {/* #90 — NOT `form.handleSubmit`. A draft is by definition incomplete, so
                        the mandatory rules registered in `ui/form.tsx` must not stand between
                        this button and the network; `POST /decas` runs `validateDeca` only when
                        `generate` is true and every mandatory column is nullable. This is also
                        what makes the `|| null` branch of each mandatory field in `toInput`
                        reachable at last. The rules still guard *Generar DeCA*, which has its
                        own gate anyway: `disabled={busy || !complete}`. */}
                    <Button
                      block
                      onClick={() => void submit(form.getValues(), false)}
                      disabled={busy}
                    >
                      {t('common.saveDraft')}
                    </Button>
                  </div>
                </div>
              </Card>

              <Notice tone="info" title={t('create.deliverReminder')}>
                <span className="small">{t('detail.qrHelp')}</span>
              </Notice>
            </aside>
          </div>
        </form>
      </DecaFormProvider>
    </FormProvider>
  )
}

/** One party's line in the validation rail: verdict, responsibility, count. */
function ProgressRow({ entry }: { entry: MandatoryProgress }) {
  const { t } = useTranslation()
  const ok = entry.missing.length === 0

  return (
    <div className="party-row">
      <Icon name={ok ? 'checkCircle' : 'alert'} size={18} className={ok ? 'ok' : 'warn'} />
      <div className="party-row__body">
        {ok ? (
          <span className="small muted">{t('create.railReady')}</span>
        ) : (
          <ul className="party-row__missing">
            {entry.missing.map((item) => (
              <li key={`${item.column}-${item.seq ?? 0}`}>
                {labelFor(item.column, t, item.seq)}
              </li>
            ))}
          </ul>
        )}
      </div>
      <span className={ok ? 'chip chip--success' : 'chip chip--warning'}>
        {entry.done}/{entry.total}
      </span>
    </div>
  )
}

/**
 * The service date, and the hour that is now optional beside it.
 *
 * Two controls rather than one `datetime-local`, because HTML has no way to leave that control's
 * time empty — and empty is exactly what has to be expressible. Art. 6(f) of the consolidated
 * Order requires the *fecha* and names no hour; the requirement came from the Client's own
 * template (`DICCIONARIO_CAMPOS` #9, *"Date and time"*) and the Client withdrew it on 2026-09-18.
 *
 * The time carries no `required`, so leaving it blank is a complete document, and the rail counts
 * it that way: `mandatoryProgress` walks `FIELDS`, which knows `fecha_realizacion` and not this.
 */
/**
 * «Soy yo» as a cell of «Datos comunes» (#137), with «Guardar en la libreta» beside it (#159). One
 * grid item, so the no-address note «Soy yo» may add stays under the buttons instead of becoming a
 * cell of its own. The spacer is a label's height, so the buttons sit level with the inputs beside
 * them. Each is named for its party, since the card holds two of each.
 */
function SoyYoCell({ party }: { party: 'cargador' | 'transportista' }) {
  const { t } = useTranslation()
  return (
    <div className="field field--soy-yo">
      <span className="field__label" aria-hidden="true">
        {'\u00a0'}
      </span>
      <div className="party-actions">
        <SoyYoAction
          columns={PARTY_COLUMNS[party]}
          accessibleName={`${t(`sections.${party}`)} · ${t('create.soyYo.action')}`}
        />
        <SaveToBookAction
          columns={PARTY_COLUMNS[party]}
          accessibleName={`${t(`sections.${party}`)} · ${t('create.saveToBook.action')}`}
        />
      </div>
    </div>
  )
}

function ServiceDateFields() {
  const { t } = useTranslation()
  const form = useFormContext<CreateFormValues>()

  return (
    <>
      <FormField column="fecha_realizacion" />
      <TextInput
        type="time"
        label={t('fields.fecha_realizacion.hora')}
        help={t('fields.fecha_realizacion.horaHelp')}
        {...form.register('fecha_realizacion_hora')}
      />
    </>
  )
}

/**
 * Section 4's fields, with «Rígido» beside the trailer's (2026-10-01): one component for both
 * layouts, as `ServiceDateFields` is. Ticking it empties the trailer and lifts its required rule —
 * `mandatory` has to follow the box, because a DOM `disabled` alone does not stop react-hook-form's
 * `required`. The server holds the same rule (`validateDeca`), so this is a convenience, never the
 * gate.
 */
function TransportFields() {
  const { t } = useTranslation()
  const form = useFormContext<CreateFormValues>()
  const rigido = form.watch('rigido')

  return (
    <>
      {fieldsForSection('transporte').map((field) =>
        field.column === 'matricula_remolque' ? (
          // One grid cell for the plate and its box: as a cell of its own, «Rígido» wrapped into the
          // next row's first column, under the tractor it does not qualify. The plate's list hangs
          // under the cell, so the box stays in sight and takes its click while the list is open.
          <div key={field.column} className="stack-sm combobox-block">
            <FormField column={field.column} disabled={rigido} mandatory={!rigido} addressBook={TRAILER_BOOK} />
            <SettingBox
              label={t('fields.matricula_remolque.rigido')}
              help={t('fields.matricula_remolque.rigidoHelp')}
              checked={rigido}
              onChange={(checked) => {
                form.setValue('rigido', checked, { shouldDirty: true })
                if (checked) {
                  form.setValue('matricula_remolque', '', { shouldDirty: true })
                  form.clearErrors('matricula_remolque')
                }
              }}
            />
          </div>
        ) : (
          <FormField
            key={field.column}
            column={field.column}
            full={field.control === 'textarea'}
            addressBook={TRANSPORT_BOOK[field.column]}
          />
        ),
      )}
    </>
  )
}

/**
 * The transport fields the address book fills (#221): the tractor's and the trailer's plates, and the
 * driver's DNI/NIE. A DeCA has no column for the driver's name, so a chosen driver fills the DNI/NIE
 * alone and nothing is saved from here.
 */
const TRAILER_BOOK: BookField = { kind: 'TRAILER', column: 'matricula_remolque' }
const TRANSPORT_BOOK: Partial<Record<string, BookField>> = {
  matricula_tractor: { kind: 'VEHICLE', column: 'matricula_tractor' },
  nif_conductor: { kind: 'DRIVER', columns: { nif: 'nif_conductor' }, field: 'nif' },
}

/** A consignment's origin or destination, which the book fills with a place (#221). */
const placeOf = (name: string): BookField => ({ kind: 'PLACE', column: name })

/** The grouped consignments, as the compact table the frame draws. */
function ConsignmentTable({
  rows,
  canRemove,
  onRemove,
}: {
  rows: { id: string }[]
  canRemove: boolean
  onRemove: (index: number) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="table-wrap">
      <table className="table table--form">
        <caption className="sr-only">{t('create.groupedConsignments')}</caption>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">{t('fields.origen_domicilio.label')}</th>
            <th scope="col">{t('fields.destino_domicilio.label')}</th>
            <th scope="col">{t('fields.naturaleza_mercancia.label')}</th>
            <th scope="col">{t('fields.peso_magnitud.label')}</th>
            <th scope="col">{t('fields.unidad_medida.label')}</th>
            <th scope="col">
              <span className="sr-only">{t('common.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id}>
              <td className="tabular">{index + 1}</td>
              {(
                [
                  'origen_domicilio',
                  'destino_domicilio',
                  'naturaleza_mercancia',
                  'peso_magnitud',
                  'unidad_medida',
                ] as const
              ).map((column) => (
                <td key={column}>
                  <FormField
                    column={column}
                    name={`consignments.${index}.${column}`}
                    errorKey={`consignments.${index + 1}.${column}`}
                    hideLabel
                    addressBook={
                      column === 'origen_domicilio' || column === 'destino_domicilio'
                        ? placeOf(`consignments.${index}.${column}`)
                        : undefined
                    }
                  />
                </td>
              ))}
              <td>
                {canRemove ? (
                  <IconButton
                    icon="x"
                    label={t('create.removeConsignment')}
                    onClick={() => onRemove(index)}
                  />
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The single-consignment fields — section 3's second half.
 *
 * ↻ This component used to take a `grouped` flag and wrap itself in a numbered
 * header with its own remove button. That branch was dead: `ConsignmentTable`
 * is what grouping renders, so the only call site has ever been `index={0}` in
 * single mode and the early `if (!grouped) return body` always returned first.
 * It was deleted rather than carried into the passenger sibling, where it would
 * have invited someone to model a repeatable services list on a branch that has
 * never run.
 *
 * 🚨 **`errorKey` is seq-keyed here too, and that is not cosmetic.** The server
 * numbers a consignment failure whatever the consignment count —
 * `validatableFromInput` stamps `seq: index + 1` over the `consignments` array
 * exactly as sent, and `validateDeca` passes that seq into every `err(…)` — so a
 * single-consignment DeCA's `origen_domicilio` comes back as `consignmentSeq: 1`,
 * never null, and `errorsByField` stores it under `consignments.1.origen_domicilio`.
 * A bare column key misses that lookup: the rail lists the failure, the input
 * stays `aria-invalid="false"` and the server's own message is never shown beside
 * the field that has to change.
 *
 * That is what the deleted `grouped` flag was hiding. Each of the five keys was a
 * ternary on it — the seq-keyed form under grouping, the bare column otherwise —
 * and since the flag was always false the seq-keyed arm never ran, so deleting the
 * dead branch kept the wrong half and removed the evidence that the other half had
 * ever been intended. The RHF path stays 0-based (`prefix`) because that is the
 * field array's own index; only the server's key is 1-based.
 */
function ConsignmentBlock({ index }: { index: number }) {
  const prefix = `consignments.${index}` as const
  /** The server's 1-based `consignmentSeq`, which `errorsByField` keys on. */
  const errorPrefix = `consignments.${index + 1}` as const

  return (
    <div className="form-grid form-grid--full">
      <FormField
        column="origen_domicilio"
        name={`${prefix}.origen_domicilio`}
        errorKey={`${errorPrefix}.origen_domicilio`}
        full
        addressBook={placeOf(`${prefix}.origen_domicilio`)}
      />
      <FormField
        column="destino_domicilio"
        name={`${prefix}.destino_domicilio`}
        errorKey={`${errorPrefix}.destino_domicilio`}
        full
        addressBook={placeOf(`${prefix}.destino_domicilio`)}
      />
      <FormField
        column="naturaleza_mercancia"
        name={`${prefix}.naturaleza_mercancia`}
        errorKey={`${errorPrefix}.naturaleza_mercancia`}
        full
      />
      <FormField
        column="peso_magnitud"
        name={`${prefix}.peso_magnitud`}
        errorKey={`${errorPrefix}.peso_magnitud`}
      />
      <FormField
        column="unidad_medida"
        name={`${prefix}.unidad_medida`}
        errorKey={`${errorPrefix}.unidad_medida`}
      />
    </div>
  )
}

/**
 * How many mandatory fields the document has, and which are still empty.
 *
 * It used to count **per liable party**, because the €401 penalty attaches per
 * party. Their testers asked for one list on 2026-09-18 and the count follows
 * it: a form being filled in by several people is one form, and two chips
 * reading 7/9 and 4/5 answered a question nobody was asking. The penalty is
 * unchanged and the notice under the row still states it. A consignment field
 * counts once per consignment, so a grouping of three raises the total honestly.
 *
 * This is a live hint, never the gate. The authoritative answer is the 422 with
 * `partyErrors` that `POST /decas` returns — and both lists sort by
 * `formPosition`, so what the user reads before submitting and what comes back
 * after are in the same order.
 */
export interface MandatoryProgress {
  done: number
  total: number
  missing: { column: string; seq?: number }[]
}

function mandatoryProgress(values: CreateFormValues, grouped: boolean): MandatoryProgress {
  const progress: MandatoryProgress = { done: 0, total: 0, missing: [] }

  const track = (filled: boolean, column: string, seq?: number) => {
    progress.total += 1
    if (filled) progress.done += 1
    else progress.missing.push(seq == null ? { column } : { column, seq })
  }

  for (const field of FIELDS) {
    if (!field.mandatory || field.scope !== 'document') continue
    // A rigid truck's trailer is not asked for: 14, or 9 + 5 per envío (spec D11).
    if (field.column === 'matricula_remolque' && values.rigido) continue
    const value = values[field.column as keyof CreateFormValues]
    track(typeof value === 'string' && value.trim() !== '', field.column)
  }

  const rows = grouped ? values.consignments : values.consignments.slice(0, 1)
  rows.forEach((row, index) => {
    for (const field of FIELDS) {
      if (!field.mandatory || field.scope !== 'consignment') continue
      const value = row[field.column as keyof ConsignmentValues]
      // An empty number input reaches here as NaN, not as '' — `valueAsNumber`
      // converts before this sees it — and NaN is neither '' nor null, so the
      // naive check counted an empty weight as supplied.
      const filled =
        typeof value === 'number'
          ? Number.isFinite(value)
          : value !== '' && value !== undefined && value !== null
      track(filled, field.column, grouped ? index + 1 : undefined)
    }
  })

  progress.missing = byFormPosition(
    progress.missing,
    (item) => item.column,
    (item) => item.seq,
  )
  return progress
}

/** The rail lists shipper and carrier fields together, so both need qualifying. */
function labelFor(column: string, t: TFunction, seq?: number): string {
  const def = fieldDef(column)
  return def ? qualifiedFieldLabel(def, t, seq) : column
}

/**
 * «Enviar DeCA a» and «Enviar automáticamente al generar el DeCA» (#219), single and grouped alike:
 * filled from the shipper's book entry when it is chosen, and changed here for this DeCA only — the
 * entry stays as it is.
 */
function EmailCard() {
  const { t } = useTranslation()
  const form = useFormContext<CreateFormValues>()
  const recipients = form.watch('emailRecipients')
  const dead = useDeadAddresses(recipients)
  return (
    <Card title={t('create.email.title')} subtitle={t('create.email.subtitle')}>
      <div className="stack">
        <EmailTagsInput
          label={t('create.email.recipients')}
          optional
          max={MAX_DECA_RECIPIENTS}
          value={recipients}
          dead={dead}
          onChange={(next) => form.setValue('emailRecipients', next, { shouldDirty: true })}
        />
        <SettingBox
          label={t('create.email.autoSend')}
          help={t('create.email.autoSendHelp')}
          checked={form.watch('emailAutoSend')}
          onChange={(checked) => form.setValue('emailAutoSend', checked, { shouldDirty: true })}
        />
      </div>
    </Card>
  )
}
